import * as React from 'react';
import type { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { FormField } from '@ooh/ui/components/form-field';
import { Input } from '@ooh/ui/components/input';
import {
  SearchableSelectField as Select,
  type SearchableSelectFieldOption as SelectOption,
} from '@ooh/ui/components/searchable-select-field';
import { Separator } from '@ooh/ui/components/separator';
import type { FormValues } from '../../../../lib/media-unit-form-schema';

interface LocationSectionProps {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  handleRegionChange: (value: string) => void;
  filteredCounties: SelectOption[];
  voivodeshipOptions: SelectOption[];
  t: TFunction<'inventory'>;
  shouldDirty?: boolean;
}

interface CoordinateFieldsProps {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  t: TFunction<'inventory'>;
}

function CoordinateFields({ register, errors, t }: CoordinateFieldsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormField
        label={t('form.fields.latitude')}
        error={errors.latitude?.message}
        testId="field-latitude"
      >
        <Input
          type="text"
          placeholder={t('form.placeholders.latitude')}
          data-testid="input-latitude"
          error={!!errors.latitude}
          {...register('latitude')}
        />
      </FormField>

      <FormField
        label={t('form.fields.longitude')}
        error={errors.longitude?.message}
        testId="field-longitude"
      >
        <Input
          type="text"
          placeholder={t('form.placeholders.longitude')}
          data-testid="input-longitude"
          error={!!errors.longitude}
          {...register('longitude')}
        />
      </FormField>
    </div>
  );
}

export function LocationSection({
  register,
  errors,
  watch,
  setValue,
  handleRegionChange,
  filteredCounties,
  voivodeshipOptions,
  t,
  shouldDirty,
}: LocationSectionProps) {
  const regionCode = watch('regionCode');
  const opts = shouldDirty ? { shouldDirty: true } : undefined;

  return (
    <Card data-testid="section-location">
      <CardHeader className="pb-4">
        <CardTitle className="text-[15px]">{t('form.sections.location')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('form.fields.region')}
            required
            error={errors.regionCode?.message}
            testId="field-region"
          >
            <Select
              options={voivodeshipOptions}
              value={regionCode ?? ''}
              onChange={handleRegionChange}
              placeholder={t('form.placeholders.region')}
              error={!!errors.regionCode}
              data-testid="select-region"
            />
          </FormField>

          <FormField
            label={t('form.fields.county')}
            error={errors.countyName?.message}
            testId="field-county"
          >
            <Select
              options={filteredCounties}
              value={watch('countyName') ?? ''}
              onChange={(v) => setValue('countyName', v, opts)}
              placeholder={
                regionCode ? t('form.placeholders.county') : t('form.placeholders.countyNoRegion')
              }
              disabled={!regionCode}
              data-testid="select-county"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('form.fields.city')}
            required
            error={errors.city?.message}
            testId="field-city"
          >
            <Input
              placeholder={t('form.placeholders.city')}
              error={!!errors.city}
              data-testid="input-city"
              {...register('city')}
            />
          </FormField>

          <FormField
            label={t('form.fields.address')}
            required
            error={errors.address?.message}
            testId="field-address"
          >
            <Input
              placeholder={t('form.placeholders.address')}
              error={!!errors.address}
              data-testid="input-address"
              {...register('address')}
            />
          </FormField>
        </div>

        <FormField
          label={t('form.fields.agglomeration')}
          error={errors.agglomeration?.message}
          testId="field-agglomeration"
        >
          <Input
            placeholder={t('form.placeholders.agglomeration')}
            data-testid="input-agglomeration"
            {...register('agglomeration')}
          />
        </FormField>

        <Separator />

        <CoordinateFields register={register} errors={errors} t={t} />
      </CardContent>
    </Card>
  );
}
